import { createContext, useContext, useState, useEffect } from "react";

const translations = {
  // ── Common ──
  "cancel": { ko: "취소", en: "Cancel" },
  "save": { ko: "저장", en: "Save" },
  "close": { ko: "닫기", en: "Close" },
  "delete": { ko: "삭제", en: "Delete" },
  "add": { ko: "추가", en: "Add" },
  "edit": { ko: "편집", en: "Edit" },
  "start": { ko: "시작", en: "Start" },
  "stop": { ko: "중지", en: "Stop" },
  "starting": { ko: "시작 중...", en: "Starting..." },
  "stopping": { ko: "중지 중...", en: "Stopping..." },
  "search": { ko: "검색", en: "Search" },
  "copy": { ko: "복사", en: "Copy" },
  "copied": { ko: "복사됨", en: "Copied" },
  "download": { ko: "다운로드", en: "Download" },
  "view": { ko: "보기", en: "View" },
  "hide": { ko: "숨기기", en: "Hide" },
  "confirm": { ko: "확인", en: "Confirm" },
  "none": { ko: "없음", en: "None" },
  "all": { ko: "전체", en: "All" },
  "running": { ko: "실행 중", en: "Running" },
  "stopped": { ko: "중지", en: "Stopped" },
  "reclaimed": { ko: "회수", en: "Reclaimed" },
  "pending": { ko: "대기", en: "Pending" },
  "approved": { ko: "승인", en: "Approved" },
  "rejected": { ko: "거절", en: "Rejected" },
  "free": { ko: "여유", en: "Free" },
  "in_use": { ko: "사용중", en: "In Use" },
  "ended": { ko: "종료", en: "Ended" },
  "logout": { ko: "로그아웃", en: "Logout" },
  "not_entered": { ko: "미입력", en: "Not entered" },
  "undecided": { ko: "미정", en: "TBD" },
  "no_data": { ko: "데이터 없음", en: "No data" },
  "error": { ko: "오류", en: "Error" },
  "success": { ko: "성공", en: "Success" },
  "required_fields": { ko: "필수 항목을 모두 입력하세요", en: "Please fill in all required fields" },

  // ── Header / Tabs ──
  "overview": { ko: "OVERVIEW", en: "OVERVIEW" },
  "tab_users": { ko: "사용자", en: "Users" },
  "tab_vms": { ko: "VM 관리", en: "VM Management" },
  "tab_gpu_status": { ko: "GPU 현황", en: "GPU Status" },
  "tab_metrics": { ko: "메트릭", en: "Metrics" },
  "tab_ports": { ko: "포트 현황", en: "Ports" },
  "tab_pending": { ko: "승인 대기", en: "Pending" },
  "tab_my_vms": { ko: "내 VM", en: "My VMs" },
  "tab_ssh": { ko: "SSH 접속", en: "SSH Access" },
  "tab_services": { ko: "포트 / 서비스", en: "Ports / Services" },
  "tab_requests": { ko: "자원 신청", en: "Request" },

  // ── Overview ──
  "system_overview": { ko: "시스템 개요", en: "System Overview" },
  "users": { ko: "사용자", en: "Users" },
  "vm": { ko: "VM", en: "VM" },
  "gpu": { ko: "GPU", en: "GPU" },
  "pending_requests": { ko: "대기 요청", en: "Pending Requests" },
  "assigned": { ko: "할당", en: "Assigned" },
  "disk_status": { ko: "디스크 현황", en: "Disk Status" },
  "host_storage": { ko: "호스트 스토리지", en: "Host Storage" },
  "vm_disk_usage": { ko: "VM별 디스크 사용량", en: "Per-VM Disk Usage" },
  "available": { ko: "가용", en: "Available" },
  "server_status": { ko: "서버 상태", en: "Server Status" },
  "live_vm_metrics": { ko: "실시간 VM 메트릭", en: "Live VM Metrics" },
  "no_vm": { ko: "VM 없음", en: "No VMs" },

  // ── VM Management ──
  "vm_management": { ko: "VM 관리", en: "VM Management" },
  "create_vm": { ko: "VM 생성", en: "Create VM" },
  "creating": { ko: "생성 중...", en: "Creating..." },
  "edit_vm": { ko: "VM 편집", en: "Edit VM" },
  "saving": { ko: "저장 중...", en: "Saving..." },
  "reclaim_vm": { ko: "VM 회수", en: "Reclaim VM" },
  "reclaiming": { ko: "회수 중...", en: "Reclaiming..." },
  "keep_data": { ko: "데이터 보존 (디스크 유지)", en: "Keep data (preserve disk)" },
  "clone": { ko: "복제", en: "Clone" },
  "cloning": { ko: "복제 중...", en: "Cloning..." },
  "mount": { ko: "마운트", en: "Mount" },
  "info": { ko: "정보", en: "Info" },
  "vm_id": { ko: "VM ID", en: "VM ID" },
  "owner": { ko: "소유자", en: "Owner" },
  "server": { ko: "서버", en: "Server" },
  "image": { ko: "이미지", en: "Image" },
  "ssh_port": { ko: "SSH 포트", en: "SSH Port" },
  "port_range": { ko: "포트 범위", en: "Port Range" },
  "status": { ko: "상태", en: "Status" },
  "metrics": { ko: "메트릭", en: "Metrics" },
  "created_at": { ko: "생성일", en: "Created" },
  "actions": { ko: "작업", en: "Actions" },
  "select_user": { ko: "사용자", en: "User" },
  "select_server": { ko: "서버", en: "Server" },
  "select_image": { ko: "이미지", en: "Image" },
  "cpu_cores": { ko: "CPU (코어)", en: "CPU (Cores)" },
  "ram_gb": { ko: "RAM (GB)", en: "RAM (GB)" },
  "disk_gb": { ko: "Disk (GB)", en: "Disk (GB)" },
  "gpu_ids": { ko: "GPU IDs (콤마 구분)", en: "GPU IDs (comma separated)" },
  "password": { ko: "초기 비밀번호", en: "Initial Password" },
  "target_server": { ko: "대상 서버", en: "Target Server" },
  "reason": { ko: "사유", en: "Reason" },
  "confirm_delete_vm": { ko: "을 완전히 삭제하시겠습니까?", en: " will be permanently deleted. Continue?" },
  "confirm_reclaim": { ko: "을 회수하시겠습니까?", en: " will be reclaimed. Continue?" },
  "ip": { ko: "IP", en: "IP" },
  "resources": { ko: "자원", en: "Resources" },

  // ── VM Info Modal ──
  "ssh_key_vm": { ko: "SSH 키 (VM 기본 키페어)", en: "SSH Key (VM default keypair)" },
  "ssh_access_key": { ko: "SSH 접속 (키 사용)", en: "SSH Access (with key)" },
  "user_access_key": { ko: "사용자 접속 (키)", en: "User Access (key)" },
  "root_access_key": { ko: "Root 접속 (키)", en: "Root Access (key)" },
  "scp_copy": { ko: "파일 복사 (SCP)", en: "File Copy (SCP)" },
  "vm_detail": { ko: "VM 상세", en: "VM Details" },
  "collaborators": { ko: "협업자", en: "Collaborators" },
  "registered_ssh_keys": { ko: "의 등록된 SSH 키", en: "'s registered SSH keys" },
  "no_ssh_keys": { ko: "등록된 SSH 키 없음", en: "No SSH keys registered" },
  "service_ports": { ko: "서비스 포트", en: "Service Ports" },
  "range": { ko: "범위", en: "Range" },
  "service_name": { ko: "서비스명", en: "Service Name" },
  "vm_port": { ko: "VM 포트", en: "VM Port" },
  "host_port": { ko: "호스트 포트", en: "Host Port" },
  "access": { ko: "접근", en: "Access" },
  "external": { ko: "외부", en: "External" },
  "tunnel": { ko: "터널", en: "Tunnel" },

  // ── Mount ──
  "disk_mount": { ko: "디스크 마운트", en: "Disk Mount" },
  "mount_desc": { ko: "서버의 디렉토리를 VM 내부에 마운트합니다.", en: "Mount a host directory inside the VM." },
  "current_mounts": { ko: "현재 마운트", en: "Current Mounts" },
  "new_mount": { ko: "새 마운트 추가", en: "Add New Mount" },
  "host_path": { ko: "호스트 경로", en: "Host Path" },
  "mount_point": { ko: "VM 마운트 포인트", en: "VM Mount Point" },
  "readonly": { ko: "읽기 전용", en: "Read Only" },
  "unmount": { ko: "해제", en: "Unmount" },
  "mounting": { ko: "마운트 중...", en: "Mounting..." },

  // ── GPU Status ──
  "gpu_status": { ko: "GPU 사용 현황", en: "GPU Status" },
  "gpu_history": { ko: "사용 히스토리", en: "Usage History" },
  "gpu_util": { ko: "사용률", en: "Utilization" },
  "vram": { ko: "VRAM", en: "VRAM" },
  "started": { ko: "시작", en: "Started" },
  "elapsed": { ko: "경과", en: "Elapsed" },
  "expected_end": { ko: "종료 예정", en: "Expected End" },
  "edit_gpu_usage": { ko: "GPU 사용 정보 수정", en: "Edit GPU Usage Info" },
  "usage_reason": { ko: "이용 사유", en: "Usage Reason" },
  "usage_reason_ph": { ko: "예: ResNet 학습, LLM fine-tuning", en: "e.g., ResNet training, LLM fine-tuning" },
  "expected_end_ph": { ko: "예: 2026-04-01 또는 미정", en: "e.g., 2026-04-01 or TBD" },
  "usage_time": { ko: "이용 시간", en: "Duration" },
  "days": { ko: "일", en: "d" },
  "hours": { ko: "시간", en: "h" },
  "minutes": { ko: "분", en: "m" },

  // ── Metrics ──
  "vm_metrics": { ko: "VM 메트릭 모니터링", en: "VM Metrics Monitoring" },
  "cpu_usage": { ko: "CPU 사용률", en: "CPU Usage" },
  "mem_usage": { ko: "메모리 사용량", en: "Memory Usage" },
  "gpu_usage": { ko: "GPU 사용률", en: "GPU Usage" },
  "gpu_detail": { ko: "GPU 메모리 / 온도 / 전력", en: "GPU Memory / Temp / Power" },
  "net_rx": { ko: "네트워크 RX (누적)", en: "Network RX (cumulative)" },
  "net_tx": { ko: "네트워크 TX (누적)", en: "Network TX (cumulative)" },
  "no_metrics": { ko: "메트릭 데이터가 아직 없습니다.", en: "No metrics data yet." },
  "no_running_vm": { ko: "실행 중인 VM이 없습니다", en: "No running VMs" },
  "metrics_wait": { ko: "메트릭 수집 대기...", en: "Waiting for metrics..." },

  // ── Ports ──
  "port_status": { ko: "포트 할당 현황", en: "Port Allocation" },
  "ssh_ports": { ko: "SSH 포트", en: "SSH Ports" },
  "service_ports_tab": { ko: "서비스 포트", en: "Service Ports" },
  "port_range_viz": { ko: "포트 범위", en: "Port Range" },
  "connect_info": { ko: "접속 정보", en: "Connection Info" },
  "no_services": { ko: "서비스 없음", en: "No services" },

  // ── Users ──
  "user_management": { ko: "사용자 관리", en: "User Management" },
  "add_user": { ko: "사용자 추가", en: "Add User" },
  "user_id": { ko: "ID", en: "ID" },
  "user_name": { ko: "이름", en: "Name" },
  "role": { ko: "역할", en: "Role" },
  "active": { ko: "활성", en: "Active" },
  "inactive": { ko: "비활성", en: "Inactive" },
  "language": { ko: "언어", en: "Language" },

  // ── Requests ──
  "new_vm_request": { ko: "새 VM 신청", en: "New VM Request" },
  "resource_change": { ko: "자원 변경", en: "Resource Change" },
  "gpu_request": { ko: "GPU 신청", en: "GPU Request" },
  "auto_create": { ko: "자동 생성", en: "Auto Create" },
  "admin_approval": { ko: "관리자 승인 필요", en: "Admin Approval Required" },
  "auto_approved": { ko: "자동 승인", en: "Auto Approved" },
  "use_gpu": { ko: "GPU 사용", en: "Use GPU" },
  "gpu_count": { ko: "GPU 수량", en: "GPU Count" },
  "gpu_available": { ko: "가용 GPU", en: "Available GPUs" },
  "gpu_shortage": { ko: "GPU가 부족합니다 — 관리자 승인이 필요합니다", en: "Insufficient GPUs — admin approval required" },
  "ram_over_limit": { ko: "G 초과 — 관리자 승인이 필요합니다", en: "G exceeded — admin approval required" },
  "vm_purpose": { ko: "VM 용도를 간략히 설명해주세요", en: "Briefly describe the VM purpose" },
  "request_submitted": { ko: "신청이 접수되었습니다", en: "Request submitted" },
  "request_history": { ko: "신청 내역", en: "Request History" },
  "no_history": { ko: "내역 없음", en: "No history" },
  "cpu_delta": { ko: "CPU 변경량", en: "CPU Change" },
  "ram_delta": { ko: "RAM 변경량 (GB)", en: "RAM Change (GB)" },
  "change_reason": { ko: "변경 사유", en: "Change Reason" },
  "no_vm_to_change": { ko: "변경할 VM이 없습니다", en: "No VMs to change" },
  "submitting": { ko: "신청 중...", en: "Submitting..." },
  "submit": { ko: "신청", en: "Submit" },

  // ── SSH ──
  "ssh_access_list": { ko: "SSH 접속 목록", en: "SSH Access List" },
  "ssh_command": { ko: "SSH 명령어 (키 사용)", en: "SSH Command (with key)" },
  "ssh_config": { ko: "SSH Config", en: "SSH Config" },
  "key_download": { ko: "키 다운로드", en: "Download Key" },
  "key_view": { ko: "키 보기", en: "View Key" },
  "key_guide": { ko: "1) 키 다운로드 → 2) chmod 600 → 3) 위 명령어로 접속", en: "1) Download key → 2) chmod 600 → 3) Connect with command above" },
  "my_pubkeys": { ko: "내 공개키 관리", en: "My Public Keys" },
  "pubkey_auto_apply": { ko: "공개키를 등록하면 모든 VM에 자동 적용됩니다", en: "Public keys are auto-applied to all VMs" },
  "add_pubkey": { ko: "공개키 추가", en: "Add Public Key" },
  "no_pubkeys": { ko: "등록된 공개키 없음", en: "No public keys registered" },
  "key_name": { ko: "이름", en: "Name" },
  "pubkey": { ko: "공개키", en: "Public Key" },

  // ── My VMs ──
  "my_vms": { ko: "내 VM 목록", en: "My VMs" },
  "shared_vms": { ko: "공유받은 VM", en: "Shared VMs" },
  "no_assigned_vm": { ko: "배정된 VM 없음 — 관리자에게 문의하거나 자원을 신청하세요", en: "No VMs assigned — contact admin or submit a request" },
  "add_collaborator": { ko: "협업자 추가", en: "Add Collaborator" },
  "collab_desc": { ko: "이 VM에 접속할 수 있는 사용자를 추가합니다.", en: "Add users who can access this VM." },
  "collab_user_id": { ko: "사용자 ID 입력", en: "Enter User ID" },
  "current_collabs": { ko: "현재 협업자", en: "Current Collaborators" },
  "remove": { ko: "제거", en: "Remove" },
  "owned_by": { ko: "소유", en: "Owned by" },

  // ── Start VM Modal ──
  "start_vm": { ko: "VM 시작", en: "Start VM" },
  "select_gpu": { ko: "GPU 선택", en: "Select GPU" },
  "gpu_direct_input": { ko: "GPU 번호 (직접 입력)", en: "GPU IDs (manual input)" },
  "no_gpu_start": { ko: "빈칸이면 GPU 없이 시작", en: "Leave empty to start without GPU" },
  "gpu_reduced_confirm": { ko: "개로 줄어듭니다. 계속하시겠습니까?", en: " GPUs. Continue?" },
  "prev_gpu": { ko: "이전 GPU", en: "Previous GPU" },

  // ── Admin Requests ──
  "request_management": { ko: "요청 관리", en: "Request Management" },
  "waiting": { ko: "대기 중", en: "Waiting" },
  "approve": { ko: "승인", en: "Approve" },
  "reject": { ko: "거절", en: "Reject" },
  "processed_history": { ko: "처리 이력", en: "Process History" },
  "no_pending": { ko: "대기 중인 요청이 없습니다.", en: "No pending requests." },
  "processed_by": { ko: "처리", en: "Processed by" },

  // ── Clone ──
  "clone_vm": { ko: "VM 복제", en: "Clone VM" },
  "clone_desc": { ko: "다른 서버로 환경 복제", en: "Clone environment to another server" },
  "clone_complete": { ko: "복제 완료", en: "Clone Complete" },
};

const LangContext = createContext();

export function LangProvider({ children, defaultLang = "ko" }) {
  const [lang, setLang] = useState(() => {
    return localStorage.getItem("gpu_fabric_lang") || defaultLang;
  });

  useEffect(() => {
    localStorage.setItem("gpu_fabric_lang", lang);
  }, [lang]);

  const t = (key) => {
    const entry = translations[key];
    if (!entry) return key;
    return entry[lang] || entry["ko"] || key;
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}
